import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Leaf, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset! Please log in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="h-10 w-10 rounded-xl bg-[#114A31] flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </span>
          <span className="font-heading text-xl font-semibold text-[#1C1C1C]">ScorpPath</span>
        </Link>
        <div className="bg-white border border-[#E5E5E5] rounded-2xl shadow-sm p-8">
          <h1 className="font-heading text-2xl font-medium text-[#1C1C1C]">Set a new password</h1>
          <p className="text-sm text-[#595D5A] mt-1">Choose a strong password you'll remember.</p>

          {!token ? (
            <p className="mt-6 text-sm text-[#EF4444]" data-testid="reset-no-token">
              This reset link is missing its token. Please use the link from your email or request a new one.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <div>
                <Label htmlFor="password" className="text-[#1C1C1C]">New password</Label>
                <Input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="mt-1.5 rounded-xl border-[#E5E5E5] py-6"
                  placeholder="At least 6 characters"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-[#1C1C1C]">Confirm password</Label>
                <Input
                  id="confirm" type="password" required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="mt-1.5 rounded-xl border-[#E5E5E5] py-6"
                  placeholder="Re-enter password"
                />
              </div>
              {error && <p className="text-sm text-[#EF4444]" data-testid="reset-error">{error}</p>}
              <Button
                type="submit" disabled={loading} data-testid="reset-submit-btn"
                className="w-full rounded-xl py-6 bg-[#114A31] hover:bg-[#1A6B46] text-white"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
          )}
          <p className="mt-6 text-center text-sm text-[#595D5A]">
            <Link to="/login" className="text-[#114A31] font-medium hover:underline">Back to log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
